/**
 * One automation's run history (Ausführungen — A4, redesigned in D11b).
 *
 * ONE ROW PER EXECUTION, READ AS A SENTENCE: what happened (status), to whom
 * (the target), about what (the subject an e-mail went out with, or the action
 * a data automation performs), and when. The row says it in words; the id of
 * anything is never its headline. Everything shown is a field the run list
 * already carries (D7) — the list asks nothing else, and in particular no
 * detail per row: a page of fifty runs is one request.
 *
 * THE TARGET IS THE RESOLVED NAME, NEVER `target_id` (D11b). A tenant the
 * server could not resolve reads "Mieter nicht verfügbar"; a move-in row from
 * before D6d, which had no target at all, reads "Historischer Sammellauf". The
 * rules are `automationRunTarget`'s, shared with the detail.
 *
 * A REASON IS A GERMAN SENTENCE. A skip or an error shows its label under the
 * status; an unknown code shows as itself rather than crashing or being folded
 * into a guess. `error_message_safe` is already sanitised by the writer that
 * stored it (A2) and is shown only when one was stored.
 *
 * EVERY ROW OPENS ITS DETAIL, AND IS A REAL BUTTON. On a wide screen a table
 * whose target cell holds the button — a click anywhere on the row opens it
 * too, for the mouse — and on a phone a list of cards that ARE buttons. Opening
 * writes `run=<id>` to the address (`onOpenRun`); the detail itself is the
 * page's (`AutomationRunDetailPanel`), so the list only says which run.
 *
 * TEST RUNS BELONG HERE. They are excluded from the 30-day statistics — a test
 * send must not move a number read as "is this healthy" — but this tab answers
 * "what happened", and a test send happened. It carries a badge.
 *
 * MOBILE IS A CARD LIST, not a horizontally scrolling table. Only ONE of the
 * two is rendered, chosen from the page's `useIsMobile()`: rendering both and
 * hiding one would put every run in the DOM twice. The table keeps
 * `overflow-x-auto` for a narrow workspace rather than amputating a column.
 *
 * "Aktualisieren" EXISTS BECAUSE OF THE CACHE. A page already read is kept for
 * the page's lifetime (`useAutomationRuns`); the button forgets this
 * automation's pages and reads the current one again. It is a read.
 */
import React from "react";
import { RefreshCw } from "lucide-react";

import { formatDateTime } from "../../../utils/datetime";
import BexioTablePagination from "../../../components/admin/bexio/workspace/BexioTablePagination";
import {
  AutomationRunStatusBadge,
  AutomationRunTestBadge,
} from "./AutomationRunStatusBadge";
import {
  automationActionLabel,
  automationTriggerLabel,
} from "./automationLabels";
import {
  automationRunReasons,
  automationRunTarget,
} from "./automationRunLabels";
import { automationRunPresentation } from "./automationRunOutcome";

const HEAD_CLASS =
  "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";
const CELL_CLASS = "px-3 py-2.5 align-top text-[12.5px] text-foreground";
const SECONDARY_BUTTON_CLASS =
  "inline-flex items-center gap-1.5 rounded-[9px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 max-md:min-h-9";

/**
 * The second line under the target: what a payment confirmation's claim was
 * about (invoice, period, total — PC6), the subject an e-mail run stored, or —
 * for an automation that changes data — the action it performs. Never a
 * subject re-rendered from a template, never a template body.
 */
function runSummary(run, actionType) {
  const presented = automationRunPresentation(run).summary;
  if (presented) return presented;
  if (run?.rendered_subject) return run.rendered_subject;
  if (actionType && actionType !== "send_email") return automationActionLabel(actionType);
  return "";
}

/**
 * The reason under the status: the stored code(s) in German, then the writer's
 * own sanitised sentence when it stored one.
 */
function runReasonText(run) {
  const labels = automationRunReasons(run).map((r) => r.label).join(" · ");
  const message = run?.error_message_safe || "";
  if (labels && message) return `${labels}: ${message}`;
  return labels || message;
}

/**
 * One sentence for assistive technology: target, status, time — and, for a
 * payment confirmation, the invoice line between target and status. On the
 * card this sentence is the button's whole name, and the invoice line is what
 * tells two runs of one tenant apart (same person, often the same minute).
 */
function runAccessibleName(run) {
  const presentation = automationRunPresentation(run);
  return [
    automationRunTarget(run).text,
    presentation.summary,
    presentation.badge.label,
    formatDateTime(run.started_at),
  ]
    .filter(Boolean)
    .join(", ");
}

function RunTime({ run }) {
  return (
    <time dateTime={run.started_at || undefined} className="whitespace-nowrap">
      {formatDateTime(run.started_at)}
    </time>
  );
}

/** Trigger, an attempt beyond the first, and the test mark. */
function RunMeta({ run }) {
  const parts = [automationTriggerLabel(run.trigger_type)];
  if (Number(run.attempt) > 1) parts.push(`Versuch ${run.attempt}`);
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-[11.5px] text-muted-foreground">
      <span>{parts.join(" · ")}</span>
      {run.is_test ? <AutomationRunTestBadge /> : null}
    </span>
  );
}

function TargetText({ run }) {
  const target = automationRunTarget(run);
  return (
    <span
      className={
        target.kind === "tenant"
          ? "font-semibold text-foreground [overflow-wrap:anywhere]"
          : "font-semibold italic text-muted-foreground"
      }
    >
      {target.text}
    </span>
  );
}

function ReasonText({ run, id }) {
  const text = runReasonText(run);
  if (!text) return null;
  return (
    <p id={id} className="m-0 mt-1 line-clamp-2 text-[11.5px] text-muted-foreground">
      {text}
    </p>
  );
}

/** The card's reason line doubles as its description (the name replaces the content). */
function runReasonId(run) {
  return runReasonText(run) ? `automation-run-reason-${run.id}` : undefined;
}

function SummaryText({ run, actionType }) {
  const text = runSummary(run, actionType);
  if (!text) return null;
  return (
    <span className="mt-0.5 block text-[11.5px] text-muted-foreground [overflow-wrap:anywhere]">
      {text}
    </span>
  );
}

function RunCard({ run, actionType, selected, onOpen }) {
  return (
    <li>
      <button
        type="button"
        data-automation-run-id={run.id}
        aria-label={runAccessibleName(run)}
        aria-describedby={runReasonId(run)}
        aria-haspopup="dialog"
        onClick={(event) => onOpen(run, event.currentTarget)}
        className={`flex w-full flex-col gap-1.5 rounded-[12px] border bg-card px-3.5 py-3 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          selected ? "border-primary ring-1 ring-primary" : "border-border"
        }`}
      >
        <span className="flex w-full items-start justify-between gap-2">
          <span className="min-w-0 text-[13px]">
            <TargetText run={run} />
            <SummaryText run={run} actionType={actionType} />
          </span>
          <AutomationRunStatusBadge
            status={run.status}
            badge={automationRunPresentation(run).badge}
          />
        </span>
        <ReasonText run={run} id={runReasonId(run)} />
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-muted-foreground">
          <RunTime run={run} />
          <RunMeta run={run} />
        </span>
      </button>
    </li>
  );
}

function RunRow({ run, actionType, selected, onOpen }) {
  return (
    <tr
      // For the mouse: the whole row opens the run. The keyboard and screen
      // readers use the real button in the target cell; its own click is the
      // one that counts, so the row ignores clicks that came from it.
      onClick={(event) => {
        if (event.target.closest("button")) return;
        const button = event.currentTarget.querySelector("[data-automation-run-id]");
        onOpen(run, button);
      }}
      className={`cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/50 ${
        selected ? "bg-primary/[0.06]" : ""
      }`}
    >
      <td className={CELL_CLASS}>
        <AutomationRunStatusBadge
          status={run.status}
          badge={automationRunPresentation(run).badge}
        />
        <ReasonText run={run} />
      </td>
      <td className={`${CELL_CLASS} min-w-[12rem]`}>
        <button
          type="button"
          data-automation-run-id={run.id}
          aria-label={runAccessibleName(run)}
          aria-haspopup="dialog"
          onClick={(event) => onOpen(run, event.currentTarget)}
          className="rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <TargetText run={run} />
        </button>
        <SummaryText run={run} actionType={actionType} />
      </td>
      <td className={CELL_CLASS}>
        <RunTime run={run} />
        <div className="mt-0.5">
          <RunMeta run={run} />
        </div>
      </td>
    </tr>
  );
}

export default function AutomationRunsTab({
  isMobile,
  actionType,
  items,
  total,
  limit,
  offset,
  loading,
  error,
  onRetry,
  onRefresh,
  onOffsetChange,
  openRunId = null,
  onOpenRun,
}) {
  const loadingText = (
    <p role="status" className="m-0 text-[12.5px] text-muted-foreground">
      Ausführungen werden geladen …
    </p>
  );

  // The first read of an automation has nothing else to show. A later page
  // keeps the pager (the total is this automation's, from its last answer),
  // so the reader can turn back while it loads.
  if (loading && !total) return loadingText;

  if (error) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="m-0 text-[12.5px] text-destructive">
          {error}
        </p>
        <button type="button" onClick={onRetry} className={SECONDARY_BUTTON_CLASS}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  const open = (run, element) => {
    if (typeof onOpenRun === "function") onOpenRun(run, element);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 text-[12px] text-muted-foreground">
          Jede Zeile ist eine Ausführung. Öffnen zeigt alle Details.
        </p>
        {typeof onRefresh === "function" ? (
          <button type="button" onClick={onRefresh} className={SECONDARY_BUTTON_CLASS}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Aktualisieren
          </button>
        ) : null}
      </div>

      {loading ? (
        loadingText
      ) : !items.length ? (
        <p className="m-0 text-[12.5px] text-muted-foreground">
          Noch keine Ausführungen.
        </p>
      ) : isMobile ? (
        <ul aria-label="Ausführungen" className="m-0 flex list-none flex-col gap-2 p-0">
          {items.map((run) => (
            <RunCard
              key={run.id}
              run={run}
              actionType={actionType}
              selected={run.id === openRunId}
              onOpen={open}
            />
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto rounded-[12px] border border-border bg-card">
          <table className="w-full border-collapse">
            <caption className="sr-only">Ausführungen</caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className={HEAD_CLASS}>Status</th>
                <th scope="col" className={HEAD_CLASS}>Ziel</th>
                <th scope="col" className={HEAD_CLASS}>Zeit</th>
              </tr>
            </thead>
            <tbody>
              {items.map((run) => (
                <RunRow
                  key={run.id}
                  run={run}
                  actionType={actionType}
                  selected={run.id === openRunId}
                  onOpen={open}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* No `onLimitChange`: omitting it hides the rows-per-page selector, and
          the page size is fixed at the endpoint's own default. One fewer
          control that changes what "50 von 300" means. */}
      <BexioTablePagination
        total={total}
        offset={offset}
        limit={limit}
        onOffsetChange={onOffsetChange}
        noun="Ausführungen"
      />
    </div>
  );
}
